import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './LandingPage.css';

const CAT_COLORS = {
  "Cafe":              "linear-gradient(135deg,#93C5FD,#3B82F6)",
  "Restaurant":        "linear-gradient(135deg,#FCA5A5,#EF4444)",
  "Grocery":           "linear-gradient(135deg,#A5B4FC,#6366F1)",
  "Saloon":            "linear-gradient(135deg,#6EE7B7,#10B981)",
  "Fruits & Vegetables": "linear-gradient(135deg,#86EFAC,#16A34A)",
  "Electronics":       "linear-gradient(135deg,#7DD3FC,#0284C7)",
  "Hardware":          "linear-gradient(135deg,#FCD34D,#D97706)",
  "Car & Bike":        "linear-gradient(135deg,#94A3B8,#475569)",
  "Medical":           "linear-gradient(135deg,#FCA5A5,#EF4444)",
  "Hospitals":         "linear-gradient(135deg,#FCA5A5,#DC2626)",
  "Gym":               "linear-gradient(135deg,#C4B5FD,#7C3AED)",
  "Entertainment":     "linear-gradient(135deg,#FDA4AF,#E11D48)",
  "Footwear":          "linear-gradient(135deg,#FDBA74,#F97316)",
  "Textiles":          "linear-gradient(135deg,#F9A8D4,#DB2777)",
  "Individual Services": "linear-gradient(135deg,#6EE7B7,#059669)",
};

const HERO_CHIPS = [
  { label: "🌟 All",         action: (nav) => nav('/userdashboard/explore') },
  { label: "🏬 Malls",       action: (nav) => nav('/userdashboard/malls') },
  { label: "☕ Cafe",         action: (nav) => nav('/userdashboard/restaurants', { state: { category: 'Cafe' } }) },
  { label: "🍽️ Restaurant",  action: (nav) => nav('/userdashboard/restaurants', { state: { category: 'Restaurant' } }) },
  { label: "✂️ Saloon",      action: (nav) => nav('/userdashboard/restaurants', { state: { category: 'Saloon' } }) },
  { label: "🛒 Grocery",     action: (nav) => nav('/userdashboard/restaurants', { state: { category: 'Grocery' } }) },
  { label: "💊 Medical",     action: (nav) => nav('/userdashboard/restaurants', { state: { category: 'Medical' } }) },
  { label: "🏋️ Gym",         action: (nav) => nav('/userdashboard/restaurants', { state: { category: 'Gym' } }) },
  { label: "🤝 Services",    action: (nav) => nav('/userdashboard/services') },
  { label: "✨ NI",           action: (nav) => nav('/userdashboard/ni') },
];

const FEATURED = [
  { name: "Cafe",        icon: "☕" },
  { name: "Restaurant",  icon: "🍽️" },
  { name: "Saloon",      icon: "✂️" },
  { name: "Grocery",     icon: "🛒" },
  { name: "Medical",     icon: "💊" },
  { name: "Gym",         icon: "🏋️" },
  { name: "Electronics", icon: "📱" },
  { name: "Hardware",    icon: "🔧" },
  { name: "Footwear",    icon: "👟" },
  { name: "Textiles",    icon: "👗" },
];

const FAQS = [
  {
    q: "What is Nearbeee?",
    a: "Nearbeee is a hyperlocal discovery platform that connects you with nearby shops, restaurants, salons, gyms, grocery stores, and service providers. Find live offers, browse catalogs, and book services — all from your neighbourhood.",
  },
  {
    q: "How do I find shops near me?",
    a: "Tap 'Explore Shops' to browse nearby shops. Allow location access for the best results — Nearbeee sorts shops by distance and shows live offers from shops closest to you.",
  },
  {
    q: "What is NI (Nearbeee Intelligence)?",
    a: "NI is Nearbeee's AI assistant. Ask anything in plain language — like 'Which cafes near me have offers today?' — and it instantly finds matching shops and live deals near you. No keywords needed.",
  },
  {
    q: "How do I register my shop on Nearbeee?",
    a: "Click 'Register Shop' and complete our 2-step form — add your shop details, upload a photo, and capture your GPS location. We use email OTP or Google login for secure access. No password required.",
  },
  {
    q: "Is Nearbeee free to use?",
    a: "Yes! Browsing shops, viewing offers, using NI, and discovering service providers is completely free for users. Shop owners can also register and list their shop at no cost.",
  },
  {
    q: "Which cities is Nearbeee available in?",
    a: "Nearbeee is live across India. The platform works in any city — shop owners anywhere in India can register, and users discover nearby shops using GPS location.",
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const goCat = (name) => navigate('/userdashboard/restaurants', { state: { category: name } });

  return (
    <div className="lp-root">

      <Helmet>
        <title>Nearbeee — Shop Local, Get Offers, Ask NI | India's Hyperlocal Discovery App</title>
        <meta name="description" content="Nearbeee is India's hyperlocal shop discovery platform. Find restaurants, salons, grocery stores, gyms &amp; service providers near you. Browse live deals, claim coupons, and use NI — our AI assistant — to search in plain language. Free for users." />
        <link rel="canonical" href="https://nearbeee.in/" />
      </Helmet>

      {/* ── HERO ── */}
      <section className="lp-hero">
        <div className="lp-hero-grid" />
        <div className="lp-hc lp-hc1" /><div className="lp-hc lp-hc2" /><div className="lp-hc lp-hc3" />
        <span className="lp-pin lp-p1">📍</span>
        <span className="lp-pin lp-p2">📍</span>
        <span className="lp-pin lp-p3">📍</span>

        <div className="lp-hero-body">
          <div className="lp-live-pill">
            <span className="lp-live-dot" />
            Live offers updating near you
          </div>
          <h1 className="lp-h1">
            Discover <span className="lp-near">shops</span><br />
            <span className="lp-orange">near you</span>
          </h1>
          <p className="lp-tag1">Shops · Service Providers · Live Offers</p>
          <p className="lp-tag2">Your neighbourhood, connected</p>
          <div className="lp-hero-btns">
            <button className="lp-btn-w" onClick={() => navigate('/userdashboard/explore')}>
              Explore Shops
            </button>
            <button className="lp-btn-o" onClick={() => navigate('/userdashboard/ni')}>
              ✨ Ask NI
            </button>
          </div>
        </div>
      </section>

      {/* ── QUICK CHIPS — outside hero so overflow:hidden doesn't clip ── */}
      <div className="lp-chips-outer">
        <div className="lp-quick-row">
          {HERO_CHIPS.map((c) => (
            <button key={c.label} className="lp-qchip" onClick={() => c.action(navigate)}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── STATS ── */}
      <div className="lp-stats">
        <div className="lp-stat"><span className="lp-snum">500+</span><span className="lp-slbl">Local Shops</span></div>
        <div className="lp-stat"><span className="lp-snum">2,400+</span><span className="lp-slbl">Active Offers</span></div>
        <div className="lp-stat"><span className="lp-snum">12K+</span><span className="lp-slbl">Happy Users</span></div>
        <div className="lp-stat"><span className="lp-snum">✨ AI</span><span className="lp-slbl">Powered Discovery</span></div>
      </div>

      {/* ── CATEGORIES (Magicpin-style) ── */}
      <section className="lp-cats-section">
        <div className="lp-section-row">
          <div>
            <div className="lp-sec-label">📦 Categories</div>
            <h2 className="lp-sec-title">What are you looking for?</h2>
          </div>
          <button className="lp-view-all" onClick={() => navigate('/userdashboard/category')}>
            View all →
          </button>
        </div>
        <div className="lp-cats-strip">
          {/* Malls first */}
          <div className="lp-cat-card lp-cat-malls" onClick={() => navigate('/userdashboard/malls')}>
            <div className="lp-cat-circle" style={{ background: "linear-gradient(135deg,#FDBA74,#F97316)" }}>
              <span className="lp-cat-emoji">🏬</span>
            </div>
            <p className="lp-cat-name">Malls</p>
          </div>
          {FEATURED.map((cat) => (
            <div key={cat.name} className="lp-cat-card" onClick={() => goCat(cat.name)}>
              <div className="lp-cat-circle" style={{ background: CAT_COLORS[cat.name] || "linear-gradient(135deg,#93C5FD,#3B82F6)" }}>
                <span className="lp-cat-emoji">{cat.icon}</span>
              </div>
              <p className="lp-cat-name">{cat.name}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── NI SECTION ── */}
      <section className="lp-ni">
        <div className="lp-ni-glow" /><div className="lp-ni-glow2" />
        <div className="lp-ni-inner">
          <div className="lp-ni-label">✨ Nearbeee Intelligence</div>
          <h2 className="lp-ni-title">
            Search smarter,<br />not harder — with <span className="lp-ni-grad">NI</span>
          </h2>
          <p className="lp-ni-sub">
            Stop scrolling. Just ask. NI finds the right local shops and live offers near you — in one answer.
          </p>
          <div className="lp-ni-compare">
            <div className="lp-ni-col lp-ni-old">
              <div className="lp-ni-col-title">😐 Without NI</div>
              <div className="lp-ni-row"><span>🔍</span><span>Scroll through hundreds of results</span></div>
              <div className="lp-ni-row"><span>😓</span><span>Miss out on live offers nearby</span></div>
              <div className="lp-ni-row"><span>📞</span><span>Call shops to check availability</span></div>
              <div className="lp-ni-row"><span>🕐</span><span>30+ minutes to find what you need</span></div>
            </div>
            <div className="lp-ni-col lp-ni-new">
              <div className="lp-ni-col-title">✨ With NI</div>
              <div className="lp-ni-row"><span>💬</span><span>Ask in plain language, get instant results</span></div>
              <div className="lp-ni-row"><span>🔥</span><span>Live offers surfaced automatically</span></div>
              <div className="lp-ni-row"><span>📋</span><span>Catalog, prices, timings — in one answer</span></div>
              <div className="lp-ni-row"><span>⚡</span><span>Answer in under 3 seconds</span></div>
            </div>
          </div>
          <div className="lp-ni-demo">
            <div className="lp-dmsg lp-dmsg-user">Which cafes near me have offers today?</div>
            <div className="lp-dmsg lp-dmsg-ni">
              <span className="lp-ni-av">✨</span>
              <span className="lp-ni-reply">
                Found <strong>3 cafes</strong> within 2 km with active offers today!<br />
                🔥 <strong>The Brew Lab</strong> (0.4 km) — 30% off brunch till 12 PM<br />
                🏷️ <strong>Café Bloom</strong> (0.3 km) — 2 beverages + snack ₹199...
              </span>
            </div>
          </div>
          <button className="lp-ni-cta" onClick={() => navigate('/userdashboard/ni')}>
            ✨ Try NI Now — It's Free
          </button>
        </div>
      </section>

      {/* ── SERVICES STRIP ── */}
      <section className="lp-services">
        <div className="lp-section-row lp-svc-row">
          <div>
            <div className="lp-sec-label">🤝 Service Providers</div>
            <h2 className="lp-sec-title">Local experts, ready to help</h2>
          </div>
          <button className="lp-view-all" onClick={() => navigate('/userdashboard/services')}>
            View all →
          </button>
        </div>
        <div className="lp-svc-strip">
          {[
            { role: "Doctor", icon: "👨‍⚕️", color: "#EFF6FF" },
            { role: "Electrician", icon: "⚡", color: "#FFF7ED" },
            { role: "Plumber", icon: "🔧", color: "#F0FDF4" },
            { role: "Photographer", icon: "📸", color: "#F5F3FF" },
            { role: "CA", icon: "📐", color: "#EFF6FF" },
            { role: "Advocate", icon: "👩‍⚖️", color: "#FFF7ED" },
          ].map((s) => (
            <div key={s.role} className="lp-svc-card" style={{ background: s.color }}
              onClick={() => navigate('/userdashboard/services')}>
              <span className="lp-svc-icon">{s.icon}</span>
              <p className="lp-svc-name">{s.role}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── WHAT'S NEXT (ROADMAP) ── */}
      <section className="lp-roadmap">
        <div className="lp-sec-label lp-roadmap-label">🚀 What's Next</div>
        <h2 className="lp-roadmap-title">We're just getting started</h2>
        <p className="lp-roadmap-sub">Live features and what's coming — the roadmap for making your neighbourhood fully connected.</p>
        <div className="lp-roadmap-grid">
          {[
            { icon: "🏷️", title: "Live Offers & Catalog",        desc: "Shop owners post real-time offers with discount %, valid dates, and descriptions. Users claim coupon codes and track their wallet.",       status: "live" },
            { icon: "✨", title: "NI — AI Local Intelligence",    desc: "Ask anything. AI uses vector search + Groq to find the right shops and offers near you — in plain English. No keywords needed.",          status: "live" },
            { icon: "🏬", title: "Malls — Group Discovery",       desc: "Explore malls with all their shops and running offers in one place. Two-tab view: Shops (with category filter) + Active Offers.",          status: "live" },
            { icon: "📽️", title: "Reel Blogs for Shops",          desc: "Shop owners will create short video blogs — new arrivals, behind-the-scenes, daily specials. Customers discover shops through reels, not just text.", status: "soon" },
            { icon: "🤖", title: "AI-Generated Shop Reels",       desc: "Upload your product photos. AI turns them into a short branded reel video automatically — no editing skills needed. Your shop, your story, generated in seconds.", status: "soon" },
            { icon: "⭐", title: "Reviews & Trust Layer",         desc: "Verified buyer reviews with photos and ratings. Users see aggregated trust scores. Shop owners get feedback to improve. Already live on mobile — web coming.", status: "progress" },
            { icon: "🎁", title: "Loyalty & Rewards",             desc: "Earn points every time you visit a shop or claim an offer. Redeem them for bigger discounts. Shops reward their loyal regulars — no app switching needed.",  status: "soon" },
            { icon: "🗺️", title: "Hyperlocal Map View",           desc: "See all shops, offers, and providers on an interactive map. Filter by category or distance. The city's offline economy, made visible.",       status: "soon" },
          ].map((f) => (
            <div key={f.title} className={`lp-road-card lp-road-${f.status}`}>
              <div className="lp-road-corner">
                {f.status === 'live'     && <span className="lp-road-badge live">Live ✓</span>}
                {f.status === 'progress' && <span className="lp-road-badge progress">In Progress</span>}
                {f.status === 'soon'     && <span className="lp-road-badge soon">Coming Soon</span>}
              </div>
              <span className="lp-road-icon">{f.icon}</span>
              <h3 className="lp-road-card-title">{f.title}</h3>
              <p className="lp-road-card-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ SECTION ── */}
      <section className="lp-faq">
        <div className="lp-faq-title-row">
          <div className="lp-faq-label">❓ FAQ</div>
          <h2 className="lp-faq-heading">Frequently Asked Questions</h2>
        </div>
        <div className="lp-faq-list">
          {FAQS.map((faq, i) => (
            <div key={i} className="lp-faq-item">
              <button
                className="lp-faq-q"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                aria-expanded={openFaq === i}
              >
                <span>{faq.q}</span>
                <span className={`lp-faq-chevron${openFaq === i ? ' open' : ''}`}>▼</span>
              </button>
              {openFaq === i && <p className="lp-faq-a">{faq.a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* ── MOBILE APP ── */}
      {/* ── MOBILE APP + FINAL CTA — unified dark bottom block ── */}
      <div className="lp-bottom-block">

        <section className="lp-mobile-app">
          <div className="lp-ma-left">
            <div className="lp-sec-label lp-ma-label">📱 Mobile App</div>
            <h2 className="lp-ma-title">Your neighbourhood<br />in your pocket</h2>
            <p className="lp-ma-sub">
              Browse shops, claim offers, chat with NI, and book services — all from the Nearbeee app. Android APK available now. iOS coming soon.
            </p>
            <div className="lp-ma-stats">
              <div className="lp-ma-stat"><span className="lp-ma-stat-val">500+</span><span className="lp-ma-stat-lbl">Shops</span></div>
              <div className="lp-ma-stat-div" />
              <div className="lp-ma-stat"><span className="lp-ma-stat-val">Live</span><span className="lp-ma-stat-lbl">Deals daily</span></div>
              <div className="lp-ma-stat-div" />
              <div className="lp-ma-stat"><span className="lp-ma-stat-val">✨ NI</span><span className="lp-ma-stat-lbl">AI Assistant</span></div>
            </div>
            <div className="lp-ma-btns">
              <a href="#" className="lp-ma-btn lp-ma-btn-apk" onClick={e => e.preventDefault()}>
                <span className="lp-ma-btn-icon">📥</span>
                <span>
                  <span className="lp-ma-btn-sub">Download</span>
                  <span className="lp-ma-btn-main">Android APK</span>
                </span>
              </a>
              <div className="lp-ma-btn lp-ma-btn-ios">
                <span className="lp-ma-btn-icon">🍎</span>
                <span>
                  <span className="lp-ma-btn-sub">iOS</span>
                  <span className="lp-ma-btn-main">Coming Soon</span>
                </span>
              </div>
            </div>
          </div>
          <div className="lp-ma-right">
            <div className="lp-phone-scene">

              {/* ── Scattered feature pills ── */}
              <div className="lp-feat-pill lp-feat-1">🏪 500+ Shops</div>
              <div className="lp-feat-pill lp-feat-2">⚡ Instant Deals</div>
              <div className="lp-feat-pill lp-feat-3">✨ NI Assistant</div>
              <div className="lp-feat-pill lp-feat-4">📍 GPS Smart</div>
              <div className="lp-feat-pill lp-feat-5">🎟️ Claim Coupons</div>

              {/* ── BACK phone — shop detail ── */}
              <div className="lp-phone lp-phone-back">
                <div className="lp-phone-island" />
                <div className="lp-phone-screen">
                  <div className="lp-ph-status">
                    <span className="lp-ph-time">9:41</span>
                    <span className="lp-ph-icons">▪▪ 🔋</span>
                  </div>
                  <div className="lp-ph-nav">
                    <span className="lp-ph-back">←</span>
                    <span className="lp-ph-nav-title">Shop Detail</span>
                    <span className="lp-ph-heart">♥</span>
                  </div>
                  <div className="lp-ph-hero lp-ph-hero-a">
                    <div className="lp-ph-hero-inner">
                      <div className="lp-ph-hero-name">Pizza Palace 🍕</div>
                      <div className="lp-ph-hero-rating">⭐ 4.8 · 124 reviews</div>
                    </div>
                  </div>
                  <div className="lp-ph-stabs">
                    <span className="lp-ph-stab lp-ph-stab-a">Menu</span>
                    <span className="lp-ph-stab">Offers</span>
                    <span className="lp-ph-stab">Info</span>
                  </div>
                  <div className="lp-ph-mitem">
                    <div className="lp-ph-mimg lp-ph-mimg-a" />
                    <div className="lp-ph-minfo"><div className="lp-ph-mname">Margherita</div><div className="lp-ph-mprice">₹299</div></div>
                    <div className="lp-ph-madd">+</div>
                  </div>
                  <div className="lp-ph-mitem">
                    <div className="lp-ph-mimg lp-ph-mimg-b" />
                    <div className="lp-ph-minfo"><div className="lp-ph-mname">Pepperoni</div><div className="lp-ph-mprice">₹349</div></div>
                    <div className="lp-ph-madd">+</div>
                  </div>
                </div>
                <div className="lp-ph-home-bar" />
              </div>

              {/* ── FRONT phone — explore ── */}
              <div className="lp-phone lp-phone-front">
                <div className="lp-phone-island" />
                <div className="lp-phone-screen">
                  <div className="lp-ph-status">
                    <span className="lp-ph-time">9:41</span>
                    <span className="lp-ph-icons">▪▪ 🔋</span>
                  </div>
                  <div className="lp-ph-top-row">
                    <div className="lp-ph-logo-f">near<span>beee</span></div>
                    <div className="lp-ph-loc-chip">📍 Bengaluru</div>
                    <div className="lp-ph-avatar" />
                  </div>
                  <div className="lp-ph-searchbar">
                    <span className="lp-ph-srch-ic">🔍</span>
                    <span className="lp-ph-srch-ph">Search shops &amp; offers…</span>
                  </div>
                  <div className="lp-ph-cats-row">
                    {[["🏪","Shops"],["🍽️","Food"],["✂️","Salon"],["💊","Med"],["☕","Cafe"]].map(([ic,lb]) => (
                      <div key={lb} className="lp-ph-cat-item">
                        <div className="lp-ph-cat-circle">{ic}</div>
                        <span className="lp-ph-cat-lbl">{lb}</span>
                      </div>
                    ))}
                  </div>
                  <div className="lp-ph-sec-lbl">🔥 Hot Deals Near You</div>
                  <div className="lp-ph-deal-card">
                    <div className="lp-ph-deal-img lp-ph-di-1" />
                    <div className="lp-ph-deal-body">
                      <span className="lp-ph-deal-badge">20% OFF</span>
                      <div className="lp-ph-deal-name">Rahul Fast Food</div>
                      <div className="lp-ph-deal-dist">📍 0.8 km · ⚡ Promoted</div>
                    </div>
                  </div>
                  <div className="lp-ph-deal-card">
                    <div className="lp-ph-deal-img lp-ph-di-2" />
                    <div className="lp-ph-deal-body">
                      <span className="lp-ph-deal-badge lp-ph-db-grn">50% OFF</span>
                      <div className="lp-ph-deal-name">Coffee Corner</div>
                      <div className="lp-ph-deal-dist">📍 1.2 km · 🏷️ Shop Deal</div>
                    </div>
                  </div>
                </div>
                <div className="lp-phone-bar-f">
                  {[["🏠","Explore"],["⊞","Cats"],["✨","NI"],["🛠️","Svcs"],["👤","Profile"]].map(([ic,lb],i) => (
                    <div key={i} className={`lp-ph-tab-f${i===0?" lp-ph-tab-f-a":""}`}>
                      <span className="lp-ph-tab-ic">{ic}</span>
                      <span className="lp-ph-tab-lb">{lb}</span>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </section>

        <div className="lp-bottom-divider" />

        {/* ── FINAL CTA ── */}
        <section className="lp-final-cta">
          <h2 className="lp-cta-title">Ready to discover your neighbourhood?</h2>
          <p className="lp-cta-sub">No signup required to explore. Join thousands of shoppers and businesses on Nearbeee.</p>
          <div className="lp-cta-groups">
            <div className="lp-cta-group">
              <p className="lp-cta-group-label">👤 For Customers</p>
              <div className="lp-cta-group-btns">
                <button className="lp-btn-w" onClick={() => navigate('/userdashboard/explore')}>
                  Explore Now
                </button>
                <button className="lp-btn-outline" onClick={() => navigate('/userlogin')}>
                  Create Account
                </button>
              </div>
            </div>
            <div className="lp-cta-vdiv" />
            <div className="lp-cta-group">
              <p className="lp-cta-group-label">🏪 For Businesses</p>
              <div className="lp-cta-group-btns">
                <button className="lp-btn-o" onClick={() => navigate('/shoplogin')}>
                  Register Shop
                </button>
                <button className="lp-btn-green" onClick={() => navigate('/userdashboard/services')}>
                  🤝 Register as Provider
                </button>
              </div>
            </div>
          </div>
        </section>

      </div>

    </div>
  );
};

export default LandingPage;
